import type { ChartPrice, Pead } from "epsm-api-types/models";
import { useCallback, useEffect, useState } from "react";
import { roundToTwoDecimals } from "@/shared/helpers/roundToTwoDecimals/roundToTwoDecimals";
import type { ChartData, ChartDataState } from "../../../types";

const X_AXIS_SHIFT = 1;
const LINE_LENGTH = {
	WITHOUT_7_POINT: 3,
	WITHOUT_20_POINT: 4,
	WITHOUT_70_POINT: 5,
} as const;

const initialState: ChartDataState = {
	movingAverage: [],
	maxMovingAverage: [],
	minMovingAverage: [],
	sigma1: [],
	sigma2: [],
	sigmaMinus1: [],
	sigmaMinus2: [],
};

const SHIFT_RIGHT_ALONG_X_AXIS = 2;

export function usePrepareDataForChart(
	relativeToSpy: boolean,
	pead: Pead | null,
	timeLine: number[],
	priceLine: ChartData,
	isMainChart: boolean,
	stdDev: boolean,
): ChartDataState {
	const [chartData, setChartData] = useState<ChartDataState>(initialState);

	const mapDataPoint = useCallback(
		(data: ChartPrice[], i: number): [number, number] | undefined => {
			const lastPointOnChart = timeLine.length - 1 - SHIFT_RIGHT_ALONG_X_AXIS;

			if (i === 0)
				return pead?.price_before_ed?.price
					? [0, pead.price_before_ed.price]
					: undefined; // first point fined by myself

			if (i === 1) return [i, roundToTwoDecimals(data[0].price)]; // second point get from api

			if (i === 2) return [i, roundToTwoDecimals(data[1].price)]; // third point get from api

			// 7th point (optional) get from api
			if (i === 7 + X_AXIS_SHIFT && data.length > LINE_LENGTH.WITHOUT_7_POINT)
				return [i, roundToTwoDecimals(data[2].price)];

			// 20th point (optional) get from api
			if (i === 20 + X_AXIS_SHIFT && data.length > LINE_LENGTH.WITHOUT_20_POINT)
				return [i, roundToTwoDecimals(data[3].price)];

			// 70th point (optional) get from api
			if (i === 70 + X_AXIS_SHIFT && data.length > LINE_LENGTH.WITHOUT_70_POINT)
				return [i, roundToTwoDecimals(data[4].price)];

			if (i === lastPointOnChart)
				return [i, roundToTwoDecimals(data.at(-1)!.price)]; // last point get from api

			return undefined;
		},
		[timeLine.length, pead?.price_before_ed?.price],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: we don't need timeline here
	const calculateMovingAverage = useCallback(
		(data: ChartPrice[]): ChartData => {
			if (
				!pead ||
				!data?.length ||
				!pead.price_before_ed ||
				(isMainChart && priceLine.length === 0)
			) {
				return [];
			}

			return timeLine
				.map((_, i) => mapDataPoint(data, i))
				.filter((el): el is [number, number] => el !== undefined);
		},
		[pead, priceLine.length, isMainChart],
	);

	useEffect(() => {
		if (!pead) return;

		const getSigmaData =
			(enabled: boolean) =>
			(spyData: ChartPrice[], normalData: ChartPrice[]): ChartData => {
				if (!enabled || !isMainChart) return [];

				return calculateMovingAverage(relativeToSpy ? spyData : normalData);
			};

		const getMovingAverageData = (
			spyData: ChartPrice[],
			normalData: ChartPrice[],
		): ChartData => {
			return calculateMovingAverage(relativeToSpy ? spyData : normalData);
		};

		const {
			avg = [],
			max = [],
			min = [],
			sigma1 = [],
			sigma2 = [],
			sigma_minus1 = [],
			sigma_minus2 = [],
			avg_spy = [],
			max_spy = [],
			min_spy = [],
			sigma1_spy = [],
			sigma2_spy = [],
			sigma_minus1_spy = [],
			sigma_minus2_spy = [],
		} = pead;

		setChartData({
			movingAverage: getMovingAverageData(avg_spy, avg),
			maxMovingAverage: getMovingAverageData(max_spy, max),
			minMovingAverage: getMovingAverageData(min_spy, min),
			sigma1: getSigmaData(stdDev)(sigma1_spy, sigma1),
			sigma2: getSigmaData(stdDev)(sigma2_spy, sigma2),
			sigmaMinus1: getSigmaData(stdDev)(sigma_minus1_spy, sigma_minus1),
			sigmaMinus2: getSigmaData(stdDev)(sigma_minus2_spy, sigma_minus2),
		});
	}, [pead, stdDev, calculateMovingAverage, isMainChart, relativeToSpy]);

	return chartData;
}
